/**
 * 组件元数据构造辅助（T04）。
 *
 * 页面作者用「逻辑 dp 布局」声明部件位置，这里把它转成
 * `ComponentMeta.anchor`（390 x 844 逻辑坐标系），保证登记进清单的坐标
 * 始终与设计稿一致；真实 DOM 的测量值（`measure.ts`）用于开发态校准。
 */
import type { ComponentMeta } from '../core/types';
import { FRAME_HEIGHT, FRAME_WIDTH } from './measure';

/** 逻辑 dp 布局盒（画布左上角为原点）。 */
export interface BoxLayout {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** 组件登记输入：除 anchor / role 外的 ComponentMeta 字段 + 布局。 */
export interface ComponentMetaInput {
  id: string;
  page: string;
  label: string;
  layout: BoxLayout;
  role?: ComponentMeta['role'];
  zIndex?: number;
  props?: string[];
  notes?: string;
}

/** 逻辑布局 → anchor。 */
export function anchorFromLayout(layout: BoxLayout): ComponentMeta['anchor'] {
  return {
    frameWidth: FRAME_WIDTH,
    frameHeight: FRAME_HEIGHT,
    x: layout.x,
    y: layout.y,
    width: layout.width,
    height: layout.height,
  };
}

/** 登记输入 → 完整 ComponentMeta。 */
export function buildComponentMeta(
  input: ComponentMetaInput,
  defaultRole: ComponentMeta['role'] = 'container',
): ComponentMeta {
  return {
    id: input.id,
    page: input.page,
    role: input.role ?? defaultRole,
    label: input.label,
    anchor: anchorFromLayout(input.layout),
    zIndex: input.zIndex ?? 0,
    ...(input.props === undefined ? {} : { props: [...input.props] }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  };
}

/** 判断两个 anchor 是否一致（用于判断是否需要覆盖登记）。 */
export function sameAnchor(a: ComponentMeta['anchor'], b: ComponentMeta['anchor']): boolean {
  return (
    a.frameWidth === b.frameWidth &&
    a.frameHeight === b.frameHeight &&
    a.x === b.x &&
    a.y === b.y &&
    a.width === b.width &&
    a.height === b.height
  );
}
